import { Injectable, Input } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { LoginService } from './login.service';
import { GeneralService } from './general.service';

import { Comment } from '../models/comment';
import { CommunityResponse } from '../models/community-response';
import { Post } from '../models/post';
import { PostableChannel } from '../models/postable-channel';
import { PollingResult } from '../models/polling.result';
import { Tag } from '../models/tag';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { addListener, launch, stop } from "devtools-detector";
import { BehaviorSubject } from 'rxjs';
import { AppUser } from '../models/app.user';
import { StorageService } from './storage.service';

@Injectable()
export class SocialService {

  private URL: string = environment.urlbase;
  private headers: HttpHeaders;

  postsPerPage: number = 20;
  commentsPerPage: number = 40;

  public killDevTools$ = new BehaviorSubject<boolean>(false);

  private pendingLikeStates: Map<number, { has_like: boolean; likes_count: number; setAt: number }> = new Map();
  private static readonly PENDING_LIKE_TTL_MS = 60_000;

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
    private storageService: StorageService
  ) {
    this.headers = this.loginService.getHeaders();
    this.loginService.loggedUser$.subscribe(
      (user: AppUser): void => {
        if (environment.production && !user?.is_producer && !environment.disableDevToolsDetector) {
          addListener(
            (isOpen: boolean): void => {
              this.killDevTools$.next(isOpen);
            }
          );
          launch();
        }
        if (environment.production && user?.is_producer) {
          stop();
          this.killDevTools$.next(false);
        }
    });
  }

  /* ===================================================
     ---------- P O S T S   F U N C T I O N S ----------
     ===================================================*/

  posts(page: number): Observable<Post[]> {
    this.headers = this.loginService.getHeaders();
    const postsExt: string = 'posts/timeline';
    const params: {[param: string]: string} = {limit: this.postsPerPage.toString(), offset: ((page - 1) * this.postsPerPage).toString()};
    return this.http.get<Post[]>(this.URL + postsExt, {headers: this.headers, params: params});
  }

  single(id: number): Observable<Post> {
    this.headers = this.loginService.getHeaders();
    const postsExt: string = 'posts/' + id;
    return this.http.get<Post>(this.URL + postsExt, {headers: this.headers});
  }

  viewAll(ids: number[]): Observable<void> {
    this.headers = this.loginService.getHeaders();
    const viewExt: string = 'viewposts';
    return this.http.post<void>(this.URL + viewExt, {posts: ids}, {headers: this.headers});
  }

  view(id: number): Observable<Post> {
    this.headers = this.loginService.getHeaders();
    const viewExt: string = 'posts/' + id + '/view';
    return this.http.post<Post>(this.URL + viewExt, null, {headers: this.headers});
  }

  likePost(model: Post): Observable<{}> {
    this.headers = this.loginService.getHeaders();
    const likeExt: string = 'posts/' + model.id + '/like';
    return this.http.post<number>(this.URL + likeExt, {}, {headers: this.headers});
  }

  unlikePost(model: Post): Observable<{}> {
    this.headers = this.loginService.getHeaders();
    const likeExt: string = 'posts/' + model.id + '/like';
    return this.http.delete<number>(this.URL + likeExt, {headers: this.headers});
  }

  setPendingLikeState(postId: number, has_like: boolean, likes_count: number): void {
    this.pendingLikeStates.set(postId, { has_like, likes_count, setAt: Date.now() });
  }

  clearPendingLikeState(postId: number): void {
    this.pendingLikeStates.delete(postId);
  }

  reconcilePendingLikes<T extends Post>(post: T): T {
    if (!post) return post;
    const pending = this.pendingLikeStates.get(post.id);
    if (!pending) return post;
    if (Date.now() - pending.setAt > SocialService.PENDING_LIKE_TTL_MS) {
      this.pendingLikeStates.delete(post.id);
      return post;
    }
    if (post.has_like === pending.has_like && post.likes_count === pending.likes_count) {
      this.pendingLikeStates.delete(post.id);
      return post;
    }
    return { ...post, has_like: pending.has_like, likes_count: pending.likes_count };
  }

  reconcilePendingLikesList<T extends Post>(posts: T[]): T[] {
    if (!posts?.length || this.pendingLikeStates.size === 0) return posts;
    return posts.map((p: T): T => this.reconcilePendingLikes(p));
  }

   savePost(model: Post): Observable<{}> {
    this.headers = this.loginService.getHeaders();
    const saveExt: string = 'posts/' + model.id + '/favorite';
    return this.http.post<{}>(this.URL + saveExt, null, {headers: this.headers});
  }

  unsavePost(model: Post): Observable<{}> {
    this.headers = this.loginService.getHeaders();
    const saveExt: string = 'posts/' + model.id + '/favorite';
    return this.http.delete<{}>(this.URL + saveExt, {headers: this.headers});
  }


  likeComment(model: Comment): Observable<number> {
    this.headers = this.loginService.getHeaders();
    const link = `${this.URL}comments/${model.id}/like`;
    return this.http.post<number>(link, {}, {headers: this.headers});
  }

  unlikeComment(model: Comment): Observable<number> {
    this.headers = this.loginService.getHeaders();
    const link = `${this.URL}comments/${model.id}/like`;
    return this.http.delete<number>(link, {headers: this.headers});
  }

  report(id: number): Observable<Post> {
    this.headers = this.loginService.getHeaders();
    const reportExt: string = 'posts/' + id + '/report';
    return this.http.post<Post>(this.URL + reportExt, null, {headers: this.headers});
  }

  post(model: Post): Observable<Post> {
    this.headers = this.loginService.getHeaders();
    const postsExt: string = 'posts';
    return this.http.post<Post>(this.URL + postsExt, model, {headers: this.headers});
  }

  delete(model: Post): Observable<void> {
    this.headers = this.loginService.getHeaders();
    const postsExt: string = 'posts/' + model.id;
    return this.http.delete<void>(this.URL + postsExt, {headers: this.headers});
  }

  /* =========================================================
     ---------- C O M M E N T S   F U N C T I O N S ----------
     =========================================================*/

  comments(postId: number, page: number): Observable<Comment[]> {
    this.headers = this.loginService.getHeaders();
    const commentExt: string = `posts/${postId}/comment?force=true`;
    const params: {[param: string]: string} = {limit: this.commentsPerPage.toString(), offset: ((page - 1) * this.commentsPerPage).toString()};
    return this.http.get<Comment[]>(this.URL + commentExt, {headers: this.headers, params: params});
  }

  getComment(model: Post, comment: number | string): Observable<Comment> {
    this.headers = this.loginService.getHeaders();
    const commentExt: string = 'posts/' + model.id + '/comment/' + comment + '?force=true';
    return this.http.get<Comment>(this.URL + commentExt, {headers: this.headers});
  }

  comment(id: number, comment: string, reply: number = null, imageKey: string = null, anonymous: boolean = false): Observable<Comment> {
    this.headers = this.loginService.getHeaders();
    const commentExt: string = 'posts/' + id + '/comment';
    const commentObj: Object = reply !== null ? {comment: comment, comment_reply_id: reply, image_key: imageKey, anonymous: anonymous} : {comment: comment, image_key: imageKey, anonymous: anonymous};
    return this.http.post<Comment>(this.URL + commentExt, commentObj, {headers: this.headers});
  }

  editComment(id: number, newComment: string, idComment: number): Observable<Comment> {
    this.headers = this.loginService.getHeaders();
    const commentExt: string = 'posts/' + id + '/comment/' + idComment;
    const commentObj: Object = {comment: newComment};
    return this.http.put<Comment>(this.URL + commentExt, commentObj, {headers: this.headers});
  }

  deleteComment(id: number, idComment: number): Observable<Comment> {
    this.headers = this.loginService.getHeaders();
    const commentExt: string = 'posts/' + id + '/comment/' + idComment;
    return this.http.delete<Comment>(this.URL + commentExt, {headers: this.headers});
  }

  pollingComments(model: Post): Observable<PollingResult> {
    return this.http.get<PollingResult>(model.polling_url + "?force=true");
  }

  pollingComment(model: Post, comment: string): Observable<Post> {
    this.headers = this.loginService.getHeaders();
    return this.http.post<Post>(model.polling_post_url + "?force=true", {comment: comment}, {headers: this.headers});
  }

  commentshow(model: Post): Observable<string> {
    this.headers = this.loginService.getHeaders();
    const likeExt: string = 'posts/' + model.id + '/commentshow';
    return this.http.post<string>(this.URL + likeExt, {}, {headers: this.headers});
  }

  commenthide(model: Post): Observable<string> {
    this.headers = this.loginService.getHeaders();
    const likeExt: string = 'posts/' + model.id + '/commenthide';
    return this.http.post<string>(this.URL + likeExt, {}, {headers: this.headers});
  }

  /* =====================================================
     ---------- C H A N N E L S   F U N C T I O N S ----------
     =====================================================*/

  tagsUsersCanUse(): Observable<Tag[]> {
    this.headers = this.loginService.getHeaders();
    return this.http
      .get<PostableChannel[]>(
        this.URL + 'community/appusercanusechannels',
        { headers: this.headers },
      )
      .pipe(
        map((channels: PostableChannel[]): Tag[] =>
          (channels || []).map((channel: PostableChannel): Tag => {
            const tag = new Tag();
            tag.id = channel.id;
            tag.name = channel.name;
            tag.private = !!channel.is_exclusive;
            tag.can_users_use = true;
            return tag;
          }),
        ),
      );
  }

  community(page: number = 1, channelId?: number): Observable<CommunityResponse> {
    this.headers = this.loginService.getHeaders();
    const params: { [param: string]: string } = {
      limit: this.postsPerPage.toString(),
      offset: ((page - 1) * this.postsPerPage).toString(),
    };
    if (channelId) {
      params['channel_id'] = channelId.toString();
    }
    return this.http.get<CommunityResponse>(this.URL + 'community', { headers: this.headers, params });
  }

  vote(model: any): Observable<any> {
    const body = {pollchoice_id: model.pollchoice_id};
    const idPost = model.idPost;
    this.headers = this.loginService.getHeaders();
    return this.http.post(`${this.URL}posts/${idPost}/vote`, body, {headers: this.headers})
  }

  getReportedPosts(): number[] {
    const reportedPosts = this.storageService.restoreObject('reported_posts');
    return Array.isArray(reportedPosts) ? reportedPosts : [];
  }

  addReportedPost(postId: number): void {
    const reportedPosts = this.getReportedPosts();
    if (!reportedPosts.includes(postId)) {
      reportedPosts.push(postId);
      this.storageService.storeObject(reportedPosts, 'reported_posts');
    }
  }
}
